import ExportedImage from 'next-image-export-optimizer';
import { useEffect, useState } from 'react';
import { BLOG_COURSES, BLOG_COURSE_IDS, type BlogCourse, type BlogCourseId } from '../../../constants/BlogCourses';
import { getBlogThumbnailBySlug } from '../../../constants/BlogsMetadata';
import { PageUrl } from '../../../constants/PageUrl';
import { BLOG_COURSE_PROGRESS_STORAGE_KEY, readCourseProgress } from '../BlogCourseNav/progress';
import styles from './index.module.scss';

const buildArticleHref = (slug: string) => `${PageUrl.BLOG_ROOT}${slug}/`;

type CourseCardAction = 'start' | 'resume' | 'again';

type CourseProgressLabel = {
  statusLabel: string | null;
  actionLabel: string;
  action: CourseCardAction;
};

const getCourseProgressLabel = (readCount: number, totalSteps: number): CourseProgressLabel => {
  if (readCount === 0) {
    return { statusLabel: null, actionLabel: '最初から読む', action: 'start' };
  }
  if (readCount >= totalSteps) {
    return { statusLabel: '読み終えました', actionLabel: 'もう一度読む', action: 'again' };
  }
  return { statusLabel: `${readCount}/${totalSteps} 本読了`, actionLabel: '続きから読む', action: 'resume' };
};

const handleCourseCardClick = (courseId: BlogCourseId, action: CourseCardAction) => {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: 'blog_course_card_click',
    course_id: courseId,
    action,
  });
};

type BlogCourseCardProps = {
  course: BlogCourse;
};

const BlogCourseCard = ({ course }: BlogCourseCardProps) => {
  const [readSlugs, setReadSlugs] = useState<Set<string>>(new Set());
  const totalSteps = course.steps.length;

  useEffect(() => {
    const syncProgress = () => {
      const progress = readCourseProgress();
      setReadSlugs(new Set(progress[course.id] ?? []));
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === BLOG_COURSE_PROGRESS_STORAGE_KEY || event.key === null) {
        syncProgress();
      }
    };

    syncProgress();
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [course.id]);

  const readCount = course.steps.filter((step) => readSlugs.has(step.slug)).length;
  const firstUnreadIndex = course.steps.findIndex((step) => !readSlugs.has(step.slug));
  const nextStepIndex = firstUnreadIndex === -1 ? 0 : firstUnreadIndex;
  const href = buildArticleHref(course.steps[nextStepIndex].slug);
  const { statusLabel, actionLabel, action } = getCourseProgressLabel(readCount, totalSteps);
  const thumbnailUrl = getBlogThumbnailBySlug(course.steps[0].slug);
  const handleClick = () => {
    handleCourseCardClick(course.id, action);
  };

  return (
    <a className={styles.card} href={href} onClick={handleClick}>
      {thumbnailUrl ? (
        <div className={styles['card-image-wrapper']}>
          <ExportedImage
            src={thumbnailUrl}
            alt={course.name}
            className={styles['card-image']}
            width={320}
            height={180}
            loading="lazy"
          />
          <span className={styles['card-total-badge']}>全{totalSteps}本</span>
        </div>
      ) : null}
      <div className={styles['card-body']}>
        <div className={styles['card-text']}>
          {statusLabel ? <span className={styles['card-status']}>{statusLabel}</span> : null}
          <span className={styles['card-name']}>{course.name}</span>
          <p className={styles['card-description']}>{course.description}</p>
        </div>
        <span className={styles['card-action']}>{actionLabel}</span>
      </div>
    </a>
  );
};

type BlogCourseListProps = {
  courseIds?: BlogCourseId[];
};

export const BlogCourseList = ({ courseIds = BLOG_COURSE_IDS }: BlogCourseListProps) => (
  <div className={styles.base}>
    {courseIds.map((courseId) => (
      <BlogCourseCard key={courseId} course={BLOG_COURSES[courseId]} />
    ))}
  </div>
);
